/**
 * DropDown
 */
import React, { memo, ReactNode } from 'react';
import Select, { Options } from 'react-select';
import StyleComponentProps from 'core/utils/types/StyleComponentProps';
import './style.css';
import classNames from 'classnames';

interface Props extends StyleComponentProps {
  label?: string;
  extraLabel?: any;
  required?: boolean;
  placeholder?: ReactNode;
  options: any[];
  onChange: any;
  justify?: string;
  isNotSearchable?: boolean;
  menuPlacement?: 'bottom' | 'top';
  getOptionLabel?: any;
  getOptionValue?: any;
  defaultValue?: any;
  value?: any;
  isMulti?: boolean;
  message?: any;
  error?: any;
  onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
  onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
  name?: string;
  labelClassName?: string;
  menuHeight?: string;
  isDisabled?: boolean;
  minWidth?: number;
  isWidth100?: boolean;
  notCloseMenuOnSelect?: boolean;
  isOptionDisabled?: (option: any, selectedValues: Options<any>) => boolean;
  isLoading?: any;
  customHeight?: string;
}

export const DropDown = memo((props: Props) => {
  const getFontSize = () => {
    if (!props.customHeight) return '14px';

    const heightValue = parseInt(props.customHeight.replace(/[^0-9]/g, ''), 10);

    if (heightValue <= 30) return '12px';
    if (heightValue <= 36) return '13px';
    return '14px';
  };

  const getChipHeight = () => {
    if (!props.customHeight) return '22px';

    const heightValue = parseInt(props.customHeight.replace(/[^0-9]/g, ''), 10);

    if (heightValue <= 30) return '18px';
    if (heightValue <= 36) return '20px';
    return '22px';
  };

  return (
    <div
      className={`
        ${props.width ? props.width : 'w-full'}
        ${props.margin ? props.margin : 'm-auto'} 
        ${props.justify ? props.justify : 'justify-start'}`}
      onClick={(e) => e.stopPropagation()}
    >
      {props.label !== undefined && (
        <label
          className={`${props.labelClassName} flex justify-between items-center min-w-max inline-block mb-1 text-sm font-[500]`}
        >
          <div>
            {props.label}
            {props.required && (
              <span className={`text-secondary-red ${props.labelClassName}`}>
                *
              </span>
            )}
          </div>
          {props?.extraLabel}
        </label>
      )}
      <Select
        menuPosition={'fixed'}
        isDisabled={props?.isDisabled}
        name={props.name}
        onBlur={props.onBlur}
        onFocus={props.onFocus}
        isMulti={props.isMulti}
        getOptionLabel={props.getOptionLabel}
        getOptionValue={props.getOptionValue}
        isSearchable={props.isNotSearchable ? false : true}
        onChange={props.onChange}
        options={props.options}
        placeholder={props.placeholder}
        closeMenuOnSelect={!props?.notCloseMenuOnSelect}
        defaultValue={props.defaultValue}
        value={props.value}
        menuPlacement={props.menuPlacement}
        menuPortalTarget={document.body}
        styles={{
          menu: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
          }),
          menuList: (provided, state) => ({
            ...provided,
            ...(!!props?.menuHeight && { maxHeight: props?.menuHeight }),
          }),
          control: (base, state) => ({
            ...base,
            boxShadow: 'none',
            borderColor: '#E0E3E7',
            minWidth: props?.minWidth || 0,
            width: props?.isWidth100 ? '110px' : 'auto',
            height: props.customHeight || '44px',
            minHeight: props.customHeight || '44px',
            padding: props.customHeight ? '0 4px' : '0 8px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            overflowX: 'hidden',
            overflowY: props.isMulti ? 'auto' : 'hidden',
            "&::-webkit-scrollbar": {
              width: "4px",
              height: "4px"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "10px"
            }
          }),
          placeholder: (base, state) => ({
            ...base,
            color: '#959EA8',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'flex',
            alignItems: 'center',
            margin: '0',
            padding: '0',
            fontSize: getFontSize(),
            lineHeight: props.customHeight ? '1' : '1.5',
            cursor: 'pointer',
            pointerEvents: 'none',
          }),
          singleValue: (base) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            fontSize: getFontSize(),
            lineHeight: props.customHeight ? '1' : '1.5',
            cursor: 'pointer',
          }),
          valueContainer: (base) => ({
            ...base,
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: props.customHeight ? '0 4px' : '2px 8px',
            height: '100%',
            cursor: 'pointer',
            overflow: 'auto',
            maxHeight: '100%',
            "&::-webkit-scrollbar": {
              width: "4px",
              height: "4px"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
              borderRadius: "10px"
            }
          }),
          multiValue: (base) => ({
            ...base,
            margin: '2px',
            height: getChipHeight(),
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#F5F7FA',
            fontSize: getFontSize(),
            ...(props.customHeight && parseInt(props.customHeight.replace(/[^0-9]/g, ''), 10) < 36 && {
              transform: 'scale(0.9)',
              margin: '1px',
            }),
          }),
          multiValueLabel: (base) => ({
            ...base,
            fontSize: getFontSize(),
            padding: '0 4px',
            lineHeight: '1',
            display: 'flex',
            alignItems: 'center',
          }),
          multiValueRemove: (base) => ({
            ...base,
            cursor: 'pointer',
            padding: '0 4px',
            '&:hover': {
              backgroundColor: '#E0E3E7',
              color: '#333',
            },
          }),
          input: (base) => ({
            ...base,
            margin: '0',
            padding: '0',
            height: props.isMulti ? 'auto' : '100%',
            fontSize: getFontSize(),
            cursor: 'pointer',
            // FIXED: Make input visible
            opacity: 1,
            position: 'relative',
            zIndex: 1,
            color: '#333',
            // Remove the conditional styling that was hiding the input
          }),
          indicatorsContainer: (base) => ({
            ...base,
            alignItems: 'center',
            height: '100%',
            position: 'sticky',
            right: 0,
            backgroundColor: props.isDisabled ? '#F5F5F5' : 'white', // Match disabled background
            opacity: 1, // Prevent grayed-out look
            cursor: props.isDisabled ? 'not-allowed' : 'pointer',
          }),

          dropdownIndicator: (base) => ({
            ...base,
            padding: props.customHeight ? '0 4px' : '0 8px',
            alignSelf: 'center',
            color: '#333', // Ensure arrow stays visible
            opacity: 1,     // Remove fade effect when disabled
            cursor: props.isDisabled ? 'not-allowed' : 'pointer',
          }),

          indicatorSeparator: (base) => ({
            ...base,
            backgroundColor: '#E0E3E7',
            margin: '0 4px',
            height: '60%', // or 100% if you want full height
            minHeight: '100%', // ensure full stretch
            alignSelf: 'stretch', // force it to fill the vertical space
            opacity: 1,
          }),
          option: (base) => ({
            ...base,
            fontSize: '14px',
            cursor: 'pointer',
          }),
          clearIndicator: (base) => ({
            ...base,
            padding: props.customHeight ? '0 4px' : '0 8px',
            cursor: 'pointer',
          }),
        }}
        classNames={{
          control: state => classNames(
            '!min-h-0',
            'flex',
            'items-center',
            'cursor-pointer',
            props.fontsize,
            {
              'text-xs': props.customHeight && parseInt(props.customHeight.replace(/[^0-9]/g, ''), 10) < 36,
            }
          ),
        }}
        isOptionDisabled={props.isOptionDisabled}
      />
      {props.message && (
        <span
          className={classNames(
            `${props.error ? 'text-secondary-red' : 'text-neutral-50'}
            mt-[4px] text-xs`,
          )}
        >
          {props.message}
        </span>
      )}
    </div>
  );
});